import React, { useState, useEffect } from 'react';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  AlertTriangle, Activity, TrendingUp, TrendingDown, Shield,
  Users, Bell, Brain, ChevronRight, AlertCircle, Pill, Home,
  UserCheck, BarChart3, Clock, CheckCircle
} from "lucide-react";

const PredictiveFallsPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  
  const [activeTab, setActiveTab] = useState('calculator');
  const [loading, setLoading] = useState(false);
  const [riskResult, setRiskResult] = useState(null);
  const [highRiskPatients, setHighRiskPatients] = useState([]);
  const [activeAlerts, setActiveAlerts] = useState([]);
  const [riskHistory, setRiskHistory] = useState(null);
  
  // Risk profile form
  const [profile, setProfile] = useState({
    patient_id: '',
    patient_name: '',
    age: 75,
    medications: '',
    diagnoses: '',
    mobility_status: 'limited_assist',
    cognitive_status: 'mild_impairment',
    fall_history: { falls_90_days: 0, had_injury: false },
    environmental_factors: [],
    recent_hospitalizations: 0,
    uses_assistive_device: false,
    lives_alone: false,
    caregiver_available_24_7: true
  });

  useEffect(() => {
    if (!authLoading && (!user || !['admin', 'administrator', 'staff'].includes(user.role))) {
      navigate('/');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    loadHighRiskPatients();
    loadActiveAlerts();
  }, []);

  const loadHighRiskPatients = async () => {
    try {
      const response = await api.get('/ehr/predictive-falls/dashboard/high-risk');
      setHighRiskPatients(response.data.patients || []);
    } catch (error) {
      console.error("Failed to load high-risk patients:", error);
    }
  };

  const loadActiveAlerts = async () => {
    try {
      const response = await api.get('/ehr/predictive-falls/alerts/active');
      setActiveAlerts(response.data.alerts || []);
    } catch (error) {
      console.error("Failed to load alerts:", error);
    }
  };

  const handleCalculateRisk = async () => {
    if (!profile.patient_name) {
      toast.error("Please enter patient name");
      return;
    }

    setLoading(true);
    try {
      const medsArray = profile.medications.split(',').map(m => m.trim()).filter(m => m);
      const diagArray = profile.diagnoses.split(',').map(d => d.trim()).filter(d => d);
      
      const response = await api.post('/ehr/predictive-falls/calculate', {
        ...profile,
        medications: medsArray,
        diagnoses: diagArray
      });
      
      setRiskResult(response.data);
      
      if (response.data.risk_level === 'critical') {
        toast.error("⚠️ CRITICAL FALL RISK - Immediate intervention required!", { duration: 5000 });
      } else if (response.data.risk_level === 'high') {
        toast.warning("High fall risk detected - See recommendations", { duration: 4000 });
      } else {
        toast.success(`Risk assessment complete: ${response.data.risk_level.toUpperCase()}`);
      }
    } catch (error) {
      console.error("Risk calculation failed:", error);
      toast.error("Failed to calculate risk score");
    } finally {
      setLoading(false);
    }
  };

  const getRiskColor = (level) => {
    switch (level) {
      case 'critical': return 'bg-red-100 text-red-700 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-300';
      case 'moderate': return 'bg-yellow-100 text-yellow-700 border-yellow-300';
      default: return 'bg-green-100 text-green-700 border-green-300';
    }
  };

  const getTrendIcon = (trend) => {
    if (trend === 'improving') return <TrendingDown className="w-4 h-4 text-green-500" />;
    if (trend === 'declining') return <TrendingUp className="w-4 h-4 text-red-500" />;
    return <Activity className="w-4 h-4 text-gray-500" />;
  };

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Brain className="w-8 h-8 text-blue-600" />
                Predictive Fall Risk Scoring
              </h1>
              <p className="text-muted-foreground mt-1">
                AI-powered proactive fall risk prediction and intervention
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/ehr-compliance')}>
              Back to EHR
            </Button>
          </div>
        </div>

        {/* Alert Banner */}
        {activeAlerts.filter(a => !a.acknowledged).length > 0 && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex items-center gap-3">
              <Bell className="w-6 h-6 text-red-600" />
              <div>
                <p className="font-semibold text-red-700">
                  {activeAlerts.filter(a => !a.acknowledged).length} Active Fall Risk Alerts
                </p>
                <p className="text-sm text-red-600">Review and acknowledge to prevent incidents</p>
              </div>
              <Button size="sm" variant="destructive" className="ml-auto" onClick={() => setActiveTab('alerts')}>
                View Alerts
              </Button>
            </div>
          </div>
        )}

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Critical Risk</p>
                  <p className="text-2xl font-bold text-red-600">
                    {highRiskPatients.filter(p => p.risk_level === 'critical').length}
                  </p>
                </div>
                <AlertTriangle className="w-8 h-8 text-red-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">High Risk</p>
                  <p className="text-2xl font-bold text-orange-600">
                    {highRiskPatients.filter(p => p.risk_level === 'high').length}
                  </p>
                </div>
                <Activity className="w-8 h-8 text-orange-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Alerts</p>
                  <p className="text-2xl font-bold text-purple-600">{activeAlerts.length}</p>
                </div>
                <Bell className="w-8 h-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Monitored</p>
                  <p className="text-2xl font-bold text-blue-600">{highRiskPatients.length}</p>
                </div>
                <Users className="w-8 h-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="calculator" data-testid="tab-calculator">
              <Brain className="w-4 h-4 mr-2" />
              Risk Calculator
            </TabsTrigger>
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">
              <BarChart3 className="w-4 h-4 mr-2" />
              High-Risk Dashboard
            </TabsTrigger>
            <TabsTrigger value="alerts" data-testid="tab-alerts">
              <Bell className="w-4 h-4 mr-2" />
              Proactive Alerts
            </TabsTrigger>
          </TabsList>

          {/* Risk Calculator Tab */}
          <TabsContent value="calculator">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Fall Risk Assessment</CardTitle>
                    <CardDescription>Enter patient data to calculate predictive fall risk score</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Patient Name *</Label>
                        <Input
                          value={profile.patient_name}
                          onChange={(e) => setProfile(prev => ({ ...prev, patient_name: e.target.value }))}
                          placeholder="Enter patient name"
                          data-testid="patient-name-input"
                        />
                      </div>
                      <div>
                        <Label>Age</Label>
                        <Input
                          type="number"
                          value={profile.age}
                          onChange={(e) => setProfile(prev => ({ ...prev, age: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Mobility Status</Label>
                        <Select 
                          value={profile.mobility_status}
                          onValueChange={(value) => setProfile(prev => ({ ...prev, mobility_status: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="independent">Independent</SelectItem>
                            <SelectItem value="supervision">Supervision</SelectItem>
                            <SelectItem value="limited_assist">Limited Assist</SelectItem>
                            <SelectItem value="extensive_assist">Extensive Assist</SelectItem>
                            <SelectItem value="total_dependence">Total Dependence</SelectItem>
                            <SelectItem value="bedbound">Bedbound</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Cognitive Status</Label>
                        <Select 
                          value={profile.cognitive_status}
                          onValueChange={(value) => setProfile(prev => ({ ...prev, cognitive_status: value }))}
                        >
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="intact">Intact</SelectItem>
                            <SelectItem value="mild_impairment">Mild Impairment</SelectItem>
                            <SelectItem value="moderate_impairment">Moderate Impairment</SelectItem>
                            <SelectItem value="severe_impairment">Severe Impairment</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label>Current Medications (comma-separated)</Label>
                      <Textarea
                        value={profile.medications}
                        onChange={(e) => setProfile(prev => ({ ...prev, medications: e.target.value }))}
                        placeholder="e.g., Alprazolam 0.5mg, Morphine 15mg, Quetiapine 25mg"
                        className="min-h-[80px]"
                      />
                    </div>

                    <div>
                      <Label>Diagnoses (comma-separated)</Label>
                      <Textarea
                        value={profile.diagnoses}
                        onChange={(e) => setProfile(prev => ({ ...prev, diagnoses: e.target.value }))}
                        placeholder="e.g., Parkinson's, Dementia, Diabetes"
                        className="min-h-[60px]"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Falls in Last 90 Days</Label>
                        <Input
                          type="number"
                          value={profile.fall_history.falls_90_days}
                          onChange={(e) => setProfile(prev => ({ 
                            ...prev, 
                            fall_history: { ...prev.fall_history, falls_90_days: parseInt(e.target.value) || 0 }
                          }))}
                        />
                      </div>
                      <div className="flex items-center gap-2 pt-6">
                        <Checkbox
                          checked={profile.fall_history.had_injury}
                          onCheckedChange={(checked) => setProfile(prev => ({
                            ...prev,
                            fall_history: { ...prev.fall_history, had_injury: checked }
                          }))}
                        />
                        <Label>Had Injury from Fall</Label>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={profile.lives_alone}
                          onCheckedChange={(checked) => setProfile(prev => ({ ...prev, lives_alone: checked }))}
                        />
                        <Label>Lives Alone</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={profile.uses_assistive_device}
                          onCheckedChange={(checked) => setProfile(prev => ({ ...prev, uses_assistive_device: checked }))}
                        />
                        <Label>Uses Assistive Device</Label>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          checked={!profile.caregiver_available_24_7}
                          onCheckedChange={(checked) => setProfile(prev => ({ ...prev, caregiver_available_24_7: !checked }))}
                        />
                        <Label>No 24/7 Caregiver</Label>
                      </div>
                    </div>

                    <Button 
                      onClick={handleCalculateRisk}
                      disabled={loading}
                      className="w-full"
                      data-testid="calculate-risk-btn"
                    >
                      {loading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      ) : (
                        <Brain className="w-4 h-4 mr-2" />
                      )}
                      Calculate Predictive Risk Score
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Results Sidebar */}
              <div className="space-y-4">
                {riskResult ? (
                  <>
                    <Card className={`border-2 ${getRiskColor(riskResult.risk_level)}`}>
                      <CardContent className="pt-6 text-center">
                        <p className="text-sm text-muted-foreground mb-2">Fall Risk Score</p>
                        <p className="text-5xl font-bold">{riskResult.overall_risk_score}</p>
                        <p className="text-sm">/100</p>
                        <Badge className={`mt-2 ${getRiskColor(riskResult.risk_level)}`}>
                          {riskResult.risk_level.toUpperCase()} RISK
                        </Badge>
                      </CardContent>
                    </Card>

                    {riskResult.alerts?.length > 0 && (
                      <Card className="border-red-200 bg-red-50">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm text-red-700">Proactive Alerts</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {riskResult.alerts.map((alert, idx) => (
                            <div key={idx} className="p-2 bg-white rounded border">
                              <p className="font-medium text-sm">{alert.title}</p>
                              <p className="text-xs text-muted-foreground">{alert.message}</p>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    {riskResult.high_risk_medications?.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2">
                            <Pill className="w-4 h-4" />
                            High-Risk Medications
                          </CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-2">
                          {riskResult.high_risk_medications.map((med, idx) => (
                            <div key={idx} className="flex justify-between text-sm">
                              <span>{med.medication}</span>
                              <Badge variant="outline">+{med.risk_points} pts</Badge>
                            </div>
                          ))}
                        </CardContent>
                      </Card>
                    )}

                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Interventions Needed</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-1 text-sm">
                          {riskResult.interventions_needed?.slice(0, 5).map((intervention, idx) => (
                            <li key={idx} className="flex items-start gap-2">
                              <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                              {intervention}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  </>
                ) : (
                  <Card>
                    <CardContent className="py-12 text-center">
                      <Brain className="w-12 h-12 mx-auto text-muted-foreground opacity-30 mb-2" />
                      <p className="text-muted-foreground">Enter patient data and calculate risk</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* High-Risk Dashboard Tab */}
          <TabsContent value="dashboard">
            <Card>
              <CardHeader>
                <CardTitle>High-Risk Patient Monitoring</CardTitle>
                <CardDescription>Patients requiring proactive fall prevention</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {highRiskPatients.map((patient, idx) => (
                    <div key={idx} className={`p-4 border rounded-lg ${
                      patient.risk_level === 'critical' ? 'bg-red-50 border-red-200' :
                      patient.risk_level === 'high' ? 'bg-orange-50 border-orange-200' :
                      'bg-yellow-50 border-yellow-200'
                    }`}>
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <span className="font-semibold text-lg">{patient.patient_name}</span>
                          <Badge className={`ml-2 ${getRiskColor(patient.risk_level)}`}>
                            {patient.risk_level.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-2xl font-bold">{patient.risk_score}</span>
                          <span className="text-sm text-muted-foreground">/100</span>
                          {getTrendIcon(patient.trend)}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-muted-foreground">Last Assessment:</span>
                          <span className="ml-1 font-medium">{patient.last_assessment}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Next Due:</span>
                          <span className="ml-1 font-medium">{patient.next_assessment}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Trend:</span>
                          <span className={`ml-1 font-medium ${
                            patient.trend === 'improving' ? 'text-green-600' :
                            patient.trend === 'declining' ? 'text-red-600' : ''
                          }`}>{patient.trend}</span>
                        </div>
                        <div>
                          <span className="text-muted-foreground">Alerts:</span>
                          <Badge variant="outline" className="ml-1">{patient.alerts_count}</Badge>
                        </div>
                      </div>
                      <div className="mt-3">
                        <span className="text-sm text-muted-foreground">Key Factors: </span>
                        {patient.key_factors?.map((factor, fidx) => (
                          <Badge key={fidx} variant="outline" className="mr-1 text-xs">{factor}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Alerts Tab */}
          <TabsContent value="alerts">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bell className="w-5 h-5" />
                  Proactive Fall Risk Alerts
                </CardTitle>
                <CardDescription>Review and acknowledge alerts to prevent falls</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {activeAlerts.map((alert, idx) => (
                    <div key={idx} className={`p-4 border rounded-lg ${
                      alert.severity === 'immediate' ? 'bg-red-50 border-red-200' :
                      alert.severity === 'urgent' ? 'bg-orange-50 border-orange-200' :
                      'bg-yellow-50 border-yellow-200'
                    } ${alert.acknowledged ? 'opacity-60' : ''}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={
                              alert.severity === 'immediate' ? 'bg-red-100 text-red-700' :
                              alert.severity === 'urgent' ? 'bg-orange-100 text-orange-700' :
                              'bg-yellow-100 text-yellow-700'
                            }>
                              {alert.severity.toUpperCase()}
                            </Badge>
                            <span className="font-semibold">{alert.title}</span>
                          </div>
                          <p className="text-sm text-muted-foreground">{alert.patient_name}</p>
                          <p className="text-sm mt-1">{alert.message}</p>
                          <div className="mt-2 flex flex-wrap gap-1">
                            {alert.actions?.map((action, aidx) => (
                              <Badge key={aidx} variant="outline" className="text-xs">{action}</Badge>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          {alert.acknowledged ? (
                            <Badge className="bg-green-100 text-green-700">Acknowledged</Badge>
                          ) : (
                            <Button size="sm" variant="outline">Acknowledge</Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                  {activeAlerts.length === 0 && (
                    <div className="text-center py-8">
                      <CheckCircle className="w-12 h-12 mx-auto text-green-500 mb-2" />
                      <p className="text-muted-foreground">No active alerts</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default PredictiveFallsPage;
